import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useParams2 } from "../contexts/ParamsContext";

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function fmtFR(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR",
    { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}

function fmtFRShort(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR",
    { day: "2-digit", month: "long" });
}

export default function BarAssociatif() {
  const params = useParams2();
  const { user } = useAuth();
  const [horaires, setHoraires] = useState([]);
  const [fermetures, setFermetures] = useState([]);
  const [creneaux, setCreneaux] = useState([]);
  const [mesCreneaux, setMesCreneaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxSemaines, setMaxSemaines] = useState(4);
  const [error, setError] = useState("");

  const estBarman = user && (user.is_admin || user.is_barman);

  const reload = () => {
    Promise.all([
      api.get("/bar/horaires"),
      api.get("/bar/creneaux"),
      estBarman ? api.get("/bar/me/creneaux") : Promise.resolve({ items: [] }),
    ]).then(([rH, rC, rM]) => {
      setHoraires(rH.horaires || []);
      setFermetures(rH.fermetures || []);
      setCreneaux(rC.items || []);
      setMaxSemaines(rC.max_semaines || 4);
      setMesCreneaux(rM.items || []);
    }).catch(() => setError("Impossible de charger les informations du bar."))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [estBarman]);

  const toggleMaPresence = async (date, suisInscrit) => {
    try {
      if (suisInscrit) {
        await api.del(`/bar/creneaux/${date}/me`);
      } else {
        await api.post(`/bar/creneaux/${date}/me`, {});
      }
      reload();
    } catch (e) {
      alert("Erreur : " + (e.message || e));
    }
  };

  return (
    <div className="container">
      <h1>{params.bar_titre || "Bar associatif"}</h1>
      <p className="muted">
        {params.bar_description ||
          "Le bar associatif vous accueille pour partager un moment convivial."}
      </p>

      {error && <div className="error">{error}</div>}

      {/* En-tête : horaires + infos */}
      <div className="biblio-header">
        <div className="card biblio-info">
          <h3 style={{ marginTop: 0 }}>🕐 Horaires d'ouverture</h3>
          {horaires.length === 0 ? (
            <p className="muted">Horaires non disponibles</p>
          ) : (
            <table className="biblio-horaires-table">
              <tbody>
                {horaires.map((h) => (
                  <tr key={h.jour} className={h.ferme ? "biblio-ferme" : ""}>
                    <td>{JOURS[h.jour]}</td>
                    <td>
                      {h.ferme ? (
                        <span className="muted">Fermé</span>
                      ) : (
                        <strong>{h.heure_ouverture} – {h.heure_fermeture}</strong>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {fermetures.length > 0 && (
            <div style={{ marginTop: "1rem", paddingTop: "0.8rem",
                          borderTop: "1px dashed rgba(0,0,0,0.1)" }}>
              <h4 style={{ fontSize: "0.95em", marginTop: 0 }}>Fermetures exceptionnelles</h4>
              {fermetures.map((f) => (
                <div key={f.id} style={{ fontSize: "0.88em", marginBottom: 4 }}>
                  <strong>Du {f.date_debut}</strong>
                  {f.date_fin !== f.date_debut && <> au <strong>{f.date_fin}</strong></>}
                  {f.motif && <span className="muted"> – {f.motif}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card biblio-info">
          <h3 style={{ marginTop: 0 }}>📍 Informations</h3>
          {params.bar_adresse && (
            <p><strong>Adresse :</strong><br/>{params.bar_adresse}</p>
          )}
          <div style={{ marginTop: "1rem", padding: "0.8rem",
                        background: "rgba(181, 75, 58, 0.06)",
                        borderRadius: 6, fontSize: "0.9em" }}>
            <strong>🍻 Vous voulez devenir barman bénévole ?</strong><br/>
            Contactez la mairie pour obtenir le statut de barman et participer
            aux soirées.
          </div>
        </div>
      </div>

      {/* Créneaux à venir */}
      <h2 style={{ marginTop: "2rem" }}>
        Prochaines ouvertures
        <span className="muted" style={{ fontSize: "0.65em", fontWeight: 400, marginLeft: 12 }}>
          ({maxSemaines} prochaines semaines)
        </span>
      </h2>

      {loading && <p className="muted">Chargement…</p>}

      {!loading && creneaux.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "2rem" }}>
          <p className="muted" style={{ margin: 0 }}>
            Aucune ouverture programmée pour le moment.
          </p>
        </div>
      )}

      {!loading && creneaux.length > 0 && (
        <div className="bar-creneaux-grid">
          {creneaux.map((c) => {
            const suisInscrit = mesCreneaux.includes(c.date);
            return (
              <div key={c.date} className={`bar-creneau-card ${suisInscrit ? "inscrit" : ""}`}>
                <div className="bar-creneau-jour">{JOURS[c.jour]}</div>
                <div className="bar-creneau-date">{fmtFRShort(c.date)}</div>
                <div className="bar-creneau-heure">
                  {c.heure_ouverture} – {c.heure_fermeture}
                </div>
                <div className="bar-creneau-presents">
                  <span className="bar-creneau-presents-num">{c.nb_presents}</span>
                  <span className="bar-creneau-presents-label">
                    {c.nb_presents > 1 ? "barmen présents" : c.nb_presents === 1 ? "barman présent" : "personne inscrite"}
                  </span>
                </div>
                {estBarman && (
                  <button
                    className={suisInscrit ? "secondary" : "btn-primary"}
                    onClick={() => toggleMaPresence(c.date, suisInscrit)}
                    style={{ width: "100%", marginTop: "0.6rem" }}>
                    {suisInscrit ? "✓ Je serai là" : "Je participe"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {estBarman && mesCreneaux.length > 0 && (
        <div className="card" style={{ marginTop: "1.5rem", background: "rgba(181, 75, 58, 0.04)" }}>
          <h3 style={{ marginTop: 0 }}>📋 Mes prochains créneaux</h3>
          <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
            {mesCreneaux.map((d) => (
              <li key={d}>{fmtFR(d)}</li>
            ))}
          </ul>
        </div>
      )}

      {!user && (
        <div className="card" style={{ marginTop: "1.5rem",
                                       background: "rgba(29,79,145,0.04)" }}>
          <p style={{ margin: 0 }}>
            <Link to="/connexion">Connectez-vous</Link> pour vous inscrire à un créneau
            (si vous avez le statut de barman bénévole).
          </p>
        </div>
      )}
    </div>
  );
}
